import { useAuth } from "@/contexts/AuthContext";
import { useStore } from "@/contexts/StoreContext/StoreContext";
import {
  excludeSupportEmployees,
  sortEmployeesByName,
} from "@/services/agenda/excludeSupportEmployees";
import { Employee } from "@/services/agenda/types";
import { employeesService } from "@/services/employees";
import { error as toastError } from "@/utils/toast.utils";
import { createContext, useEffect, useMemo, useState } from "react";

interface EmployeesContextValue {
  employees: Employee[];
  loading: boolean;
}

export const EmployeesContext = createContext<EmployeesContextValue>({
  employees: [],
  loading: true,
});

export const EmployeesProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const { selectedStoreInScheduler, allStores, store } = useStore();
  const { data: authData } = useAuth();

  // Determine which store's externalCode to use for filtering
  const storeExternalCode = useMemo(() => {
    if (selectedStoreInScheduler && allStores.length > 0) {
      const selectedStore = allStores.find(
        s => s.id === selectedStoreInScheduler,
      );
      if (selectedStore) {
        return selectedStore.externalCode;
      }
    }
    // No `localStorage` fallback: that key is shared with every tab and frame on
    // this origin, and it is only written when a store resolves from a URL
    // hub_code, so it can name a different store than the one actually in use.
    return store?.externalCode || null;
  }, [selectedStoreInScheduler, allStores, store?.externalCode]);

  useEffect(() => {
    const fetchEmployees = async () => {
      // No resolved store means there is nothing to filter by. Listing every
      // employee would silently mix stores, so the list stays empty while the
      // rest of the app reports the store as unavailable.
      if (!storeExternalCode) {
        setEmployees([]);
        setLoading(false);
        return;
      }

      try {
        const { content } = await employeesService.getEmployees();

        const activeForStore = content.filter(
          employee =>
            employee.isActive &&
            employee.stores.some(
              employeeStore => employeeStore.externalCode === storeExternalCode,
            ),
        );

        setEmployees(
          sortEmployeesByName(
            excludeSupportEmployees(activeForStore, authData?.id),
          ),
        );
      } catch (error) {
        console.error("Error fetching employees:", error);
        toastError(
          "Não foi possível carregar os funcionários. Atualize a página e tente novamente.",
        );
      } finally {
        setLoading(false);
      }
    };
    fetchEmployees();
  }, [authData?.id, storeExternalCode]);

  const value = useMemo(() => ({ employees, loading }), [employees, loading]);

  return (
    <EmployeesContext.Provider value={value}>
      {children}
    </EmployeesContext.Provider>
  );
};
